import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { ErrorBoundary, RouteResetErrorBoundary } from './components/ErrorBoundary';
import { PrivateRoute } from './components/PrivateRoute';
import DashboardLayout from './components/DashboardLayout';
import LoginTemplate from './pages/LoginTemplate';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import SelectPipeline from './pages/SelectPipeline';
import DimensionVerificationWorkflow from './pages/pipelines/DimensionVerificationWorkflow';
import MaterialWorkflow from './pages/pipelines/MaterialWorkflow';
import PartCategoryWorkflow from './pages/pipelines/PartCategoryWorkflow';
import GenericPromptWorkflow from './pages/pipelines/GenericPromptWorkflow';
import Upload from './pages/CreateJob';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import JobValidation from './pages/JobValidation';
import Profile from './pages/Profile';
import Analytics from './pages/Analytics';
import Admin from './pages/Admin';
import AdminUsers from './pages/AdminUsers';
import AdminOrganizations from './pages/AdminOrganizations';
import ActivityLogs from './pages/ActivityLogs';
import SystemSettings from './pages/SystemSettings';
import AdminSystemConfig from './pages/AdminSystemConfig';
import AdminAIModelConfig from './pages/AdminAIModelConfig';
import DataUtilities from './pages/DataUtilities';
import DesignSystemDemo from './pages/DesignSystemDemo';
import Docs from './pages/Docs';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
      staleTime: 0,
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
      <ConfirmProvider>
        <ErrorBoundary>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/* Router-aware boundary: a render error on any page resets when the
                route changes, so navigation / browser Back escapes the error
                screen instead of trapping the user (the outer ErrorBoundary
                above only catches catastrophic Router/Provider failures). */}
            <RouteResetErrorBoundary>
            <Routes>
            <Route path="/login" element={<LoginTemplate />} />
            <Route path="/register" element={<Register />} />
          
          {/* Protected routes with dashboard layout */}
          <Route path="/" element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="select-pipeline" element={<SelectPipeline />} />
            <Route path="upload" element={<Upload />} />
            <Route path="pipelines/dimensions" element={<DimensionVerificationWorkflow />} />
            <Route path="pipelines/material" element={<MaterialWorkflow />} />
            <Route path="pipelines/part-category" element={<PartCategoryWorkflow />} />
            <Route path="pipelines/generic" element={<GenericPromptWorkflow />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="jobs/:id/validate" element={<JobValidation />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="data-utilities" element={<DataUtilities />} />
            <Route path="profile" element={<Profile />} />
            <Route path="docs" element={<Docs />} />
            {/* Dev-only: design system reference */}
            <Route path="design-system-demo" element={<DesignSystemDemo />} />
            <Route path="admin" element={<Admin />} />
            <Route path="admin/users" element={<AdminUsers />} />
            <Route path="admin/organizations" element={<AdminOrganizations />} />
            <Route path="admin/activity" element={<ActivityLogs />} />
            <Route path="admin/settings" element={<SystemSettings />} />
            <Route path="admin/system-config" element={<AdminSystemConfig />} />
            <Route path="admin/ai-model-config" element={<AdminAIModelConfig />} />
            <Route index element={<Navigate to="/dashboard" replace />} />
          </Route>
          </Routes>
            </RouteResetErrorBoundary>
        </AuthProvider>
          </QueryClientProvider>
      </Router>
        </ErrorBoundary>
      </ConfirmProvider>
        </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
